import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  TextField,
  Grid,
  Divider,
  Alert,
  Snackbar,
  IconButton,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link as MuiLink,
  CircularProgress,
  Card,
  CardContent,
  Stack,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  CloudUpload as UploadIcon,
  AccountBalance as BankIcon,
  CurrencyExchange as CryptoIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Security as SecurityIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import QRCode from 'react-qr-code';
import { Link } from 'react-router-dom';

// Premium color palette
const colors = {
  primaryDark: '#0D1B2A',
  primaryMid: '#1B263B',
  primaryLight: '#415A77',
  accent: '#FF6B35',
  accentLight: '#FF8C5A',
  accentGlow: 'rgba(255, 107, 53, 0.3)',
  teal: '#00B4D8',
  tealLight: '#48CAE4',
  tealGlow: 'rgba(0, 180, 216, 0.2)',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  lightGray: '#E9ECEF',
  midGray: '#778DA9',
  darkGray: '#415A77',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

interface PaymentConfig {
  usdc: {
    address: string;
    network: string;
    symbol: string;
  };
  supportEmail: string;
  bankDetails: {
    available: boolean;
  };
  policies: {
    settlementTerms: string;
    refundPolicy: string;
    usdcValuation: string;
    disputeContact: string;
  };
}

const PaymentsPage: React.FC = () => {
  // Config state
  const [config, setConfig] = useState<PaymentConfig | null>(null);
  const [loading, setLoading] = useState(true);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    clientName: '',
    clientEmail: '',
    clientCompany: '',
    amountUsd: '',
    description: '',
    railPreference: 'USDC' as 'USDC' | 'ACH' | 'WIRE',
  });
  const [invoiceSubmitting, setInvoiceSubmitting] = useState(false);
  const [createdInvoice, setCreatedInvoice] = useState<{ invoiceId: string; amount: number } | null>(null);

  // Proof submission states
  const [proofForm, setProofForm] = useState({
    invoiceId: '',
    txHash: '',
    referenceNumber: '',
    notes: '',
  });
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofSubmitting, setProofSubmitting] = useState(false);

  // Bank details state
  const [bankDetails, setBankDetails] = useState<any>(null);
  const [bankDetailsLoading, setBankDetailsLoading] = useState(false);
  const [bankEmail, setBankEmail] = useState('');

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      const res = await fetch('/api/payments/config');
      const data = await res.json();
      if (data.success) {
        setConfig(data.data);
      }
    } catch (error) {
      console.error('Failed to load payments config:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: `${label} copied to clipboard`, severity: 'success' });
  };

  const handleInvoiceSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setInvoiceSubmitting(true);

    try {
      const res = await fetch('/api/payments/create', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(invoiceForm),
      });
      const data = await res.json();

      if (data.success) {
        setCreatedInvoice({ invoiceId: data.data.invoiceId, amount: data.data.amount });
        setProofForm(prev => ({ ...prev, invoiceId: data.data.invoiceId }));
        setSnackbar({ open: true, message: 'Payment request created!', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: data.error || 'Failed to create payment request', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
    } finally {
      setInvoiceSubmitting(false);
    }
  };

  const handleProofSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setProofSubmitting(true);

    try {
      const formData = new FormData();
      formData.append('invoiceId', proofForm.invoiceId);
      if (proofForm.txHash) formData.append('txHash', proofForm.txHash);
      if (proofForm.referenceNumber) formData.append('referenceNumber', proofForm.referenceNumber);
      if (proofForm.notes) formData.append('notes', proofForm.notes);
      if (proofFile) formData.append('proofFile', proofFile);

      const res = await fetch('/api/payments/proof', {
        method: 'POST',
        body: formData,
      });
      const data = await res.json();

      if (data.success) {
        setSnackbar({ open: true, message: 'Proof submitted successfully!', severity: 'success' });
        // Redirect to receipt page
        window.location.href = `/payments/receipt/${proofForm.invoiceId}`;
      } else {
        setSnackbar({ open: true, message: data.error || 'Failed to submit proof', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
    } finally {
      setProofSubmitting(false);
    }
  };

  const requestBankDetails = async () => {
    if (!bankEmail) {
      setSnackbar({ open: true, message: 'Please enter your email', severity: 'error' });
      return;
    }
    setBankDetailsLoading(true);

    try {
      const res = await fetch('/api/payments/request-bank-details', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: bankEmail, 
          invoiceId: createdInvoice?.invoiceId || proofForm.invoiceId 
        }),
      });
      const data = await res.json();

      if (data.success) {
        setBankDetails(data.data.bank);
        setSnackbar({ open: true, message: 'Bank details retrieved', severity: 'success' });
      } else {
        setSnackbar({ open: true, message: data.error || 'Failed to get bank details', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: 'Network error. Please try again.', severity: 'error' });
    } finally {
      setBankDetailsLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.primaryDark 
      }}>
        <CircularProgress sx={{ color: colors.teal }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${colors.primaryDark} 0%, ${colors.primaryMid} 100%)`,
    }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'rgba(13, 27, 42, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                component="a"
                href="/landing.html"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.accent} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                }}
              >
                REDEX
              </Typography>
              <Chip
                label="PAYMENTS"
                size="small"
                sx={{
                  background: 'rgba(0, 180, 216, 0.15)',
                  color: colors.tealLight,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                  border: `1px solid rgba(0, 180, 216, 0.3)`,
                }}
              />
            </Stack>
            <Button
              component="a"
              href="/landing.html"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                color: colors.midGray, 
                textTransform: 'none',
                '&:hover': { color: colors.white },
              }}
            >
              Back to Home
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 6 }}>
        {/* Hero Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              color: colors.white,
              mb: 2,
            }}
          >
            Secure Payment Portal
          </Typography>
          <Typography
            variant="h6"
            sx={{ color: colors.midGray, maxWidth: 600, mx: 'auto' }}
          >
            Pay with USDC (ERC-20) for instant settlement, or use traditional bank transfer.
            Choose the method that works best for you.
          </Typography>
        </Box>

        {/* Step 1: Create Payment Request */}
        {!createdInvoice && (
          <Paper
            sx={{
              p: 4,
              mb: 4,
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              border: `1px solid rgba(255, 255, 255, 0.1)`,
              borderRadius: 3,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center" mb={3}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  background: colors.teal,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: colors.white,
                  fontWeight: 700,
                }}
              >
                1
              </Box>
              <Typography variant="h5" sx={{ color: colors.white, fontWeight: 600 }}>
                Create Payment Request
              </Typography>
            </Stack>

            <form onSubmit={handleInvoiceSubmit}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    label="Your Name"
                    value={invoiceForm.clientName}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, clientName: e.target.value }))}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    type="email"
                    label="Email Address"
                    value={invoiceForm.clientEmail}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, clientEmail: e.target.value }))}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    label="Company (Optional)"
                    value={invoiceForm.clientCompany}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, clientCompany: e.target.value }))}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <TextField
                    fullWidth
                    required
                    type="number"
                    label="Amount (USD)"
                    value={invoiceForm.amountUsd}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, amountUsd: e.target.value }))}
                    inputProps={{ min: 1, step: '0.01' }}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    label="Description / Invoice Reference"
                    value={invoiceForm.description}
                    onChange={(e) => setInvoiceForm(prev => ({ ...prev, description: e.target.value }))}
                    sx={textFieldStyle}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <FormControl fullWidth sx={textFieldStyle}>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={invoiceForm.railPreference}
                      label="Payment Method"
                      onChange={(e) => setInvoiceForm(prev => ({ ...prev, railPreference: e.target.value as any }))}
                    >
                      <MenuItem value="USDC">USDC (ERC-20) — Recommended</MenuItem>
                      <MenuItem value="ACH">ACH Bank Transfer</MenuItem>
                      <MenuItem value="WIRE">Wire Transfer</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={invoiceSubmitting}
                    startIcon={invoiceSubmitting ? <CircularProgress size={20} /> : <ReceiptIcon />}
                    sx={{
                      height: 56,
                      background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                      fontWeight: 600,
                      fontSize: '1rem',
                      boxShadow: `0 4px 20px ${colors.accentGlow}`,
                      '&:hover': {
                        boxShadow: `0 6px 30px ${colors.accentGlow}`,
                      },
                    }}
                  >
                    {invoiceSubmitting ? 'Creating...' : 'Generate Payment Request'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}

        {/* Step 2: Payment Instructions (after invoice created) */}
        {createdInvoice && (
          <>
            <Alert 
              severity="success" 
              sx={{ 
                mb: 4, 
                background: 'rgba(16, 185, 129, 0.15)',
                border: `1px solid ${colors.success}`,
                color: colors.white,
                '& .MuiAlert-icon': { color: colors.success }
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Payment Request Created: {createdInvoice.invoiceId}
              </Typography>
              <Typography variant="body2">
                Amount: ${createdInvoice.amount.toLocaleString()} USD
              </Typography>
            </Alert>

            <Grid container spacing={4}>
              {/* USDC Payment */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid rgba(0, 180, 216, 0.3)`,
                    borderRadius: 3,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                    <CryptoIcon sx={{ color: colors.teal, fontSize: 32 }} />
                    <Box>
                      <Typography variant="h6" sx={{ color: colors.white, fontWeight: 600 }}>
                        Pay with USDC
                      </Typography>
                      <Chip label="Recommended" size="small" sx={{ 
                        background: colors.success, 
                        color: colors.white,
                        fontSize: '0.7rem',
                      }} />
                    </Box>
                  </Stack>

                  <Typography variant="body2" sx={{ color: colors.midGray, mb: 3 }}>
                    Send USDC (ERC-20) to the address below. Include your invoice ID in the transaction memo if your wallet supports it.
                  </Typography>

                  {/* QR Code */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mb: 3,
                    p: 2,
                    background: colors.white,
                    borderRadius: 2,
                    width: 'fit-content',
                    mx: 'auto',
                  }}>
                    <QRCode value={config?.usdc.address || ''} size={160} />
                  </Box>

                  {/* Address */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ color: colors.midGray }}>
                      USDC Address (ERC-20)
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid rgba(255, 255, 255, 0.1)`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '0.85rem',
                          color: colors.tealLight,
                          wordBreak: 'break-all',
                        }}
                      >
                        {config?.usdc.address}
                      </Typography>
                      <Tooltip title="Copy Address">
                        <IconButton
                          onClick={() => copyToClipboard(config?.usdc.address || '', 'Address')}
                          sx={{ color: colors.teal }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </Box>

                  {/* Reference */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ color: colors.midGray }}>
                      Payment Reference (include in memo if possible)
                    </Typography>
                    <Paper
                      sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'rgba(0, 0, 0, 0.3)',
                        border: `1px solid rgba(255, 255, 255, 0.1)`,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: '1rem',
                          color: colors.accent,
                          fontWeight: 600,
                        }}
                      >
                        {createdInvoice.invoiceId}
                      </Typography>
                      <Tooltip title="Copy Reference">
                        <IconButton
                          onClick={() => copyToClipboard(createdInvoice.invoiceId, 'Reference')}
                          sx={{ color: colors.accent }}
                        >
                          <CopyIcon />
                        </IconButton>
                      </Tooltip>
                    </Paper>
                  </Box>

                  <Alert severity="info" sx={{ 
                    mb: 3,
                    background: 'rgba(0, 180, 216, 0.1)',
                    border: `1px solid rgba(0, 180, 216, 0.3)`,
                    color: colors.white,
                    '& .MuiAlert-icon': { color: colors.teal }
                  }}>
                    <Typography variant="body2">
                      <strong>Network:</strong> {config?.usdc.network}
                      <br />
                      <strong>Token:</strong> USDC (ERC-20)
                    </Typography>
                  </Alert>
                </Paper>
              </Grid>

              {/* Bank Transfer */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Paper
                  sx={{
                    p: 4,
                    height: '100%',
                    background: 'rgba(255, 255, 255, 0.03)',
                    backdropFilter: 'blur(10px)',
                    border: `1px solid rgba(255, 255, 255, 0.1)`,
                    borderRadius: 3,
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                    <BankIcon sx={{ color: colors.midGray, fontSize: 32 }} />
                    <Typography variant="h6" sx={{ color: colors.white, fontWeight: 600 }}>
                      Pay via Bank Transfer
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ color: colors.midGray, mb: 3 }}>
                    For ACH or wire transfers, request our bank details securely below.
                    Include your invoice ID in the payment memo.
                  </Typography>

                  {!bankDetails ? (
                    <Box>
                      <TextField
                        fullWidth
                        label="Your Email"
                        type="email"
                        value={bankEmail}
                        onChange={(e) => setBankEmail(e.target.value)}
                        sx={{ ...textFieldStyle, mb: 2 }}
                        placeholder="Enter your email to receive bank details"
                      />
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={requestBankDetails}
                        disabled={bankDetailsLoading || !config?.bankDetails.available}
                        startIcon={bankDetailsLoading ? <CircularProgress size={20} /> : <SecurityIcon />}
                        sx={{
                          borderColor: colors.midGray,
                          color: colors.white,
                          py: 1.5,
                          '&:hover': {
                            borderColor: colors.teal,
                            background: 'rgba(0, 180, 216, 0.1)',
                          },
                        }}
                      >
                        {bankDetailsLoading ? 'Loading...' : 'Request Bank Details'}
                      </Button>
                      {!config?.bankDetails.available && (
                        <Typography variant="caption" sx={{ color: colors.warning, mt: 1, display: 'block' }}>
                          Bank transfer is not yet configured. Please use USDC or contact support.
                        </Typography>
                      )}
                    </Box>
                  ) : (
                    <Box>
                      <Alert severity="warning" sx={{ 
                        mb: 2,
                        background: 'rgba(245, 158, 11, 0.1)',
                        border: `1px solid ${colors.warning}`,
                        color: colors.white,
                        '& .MuiAlert-icon': { color: colors.warning }
                      }}>
                        <Typography variant="body2">
                          Keep this information secure. Do not share publicly.
                        </Typography>
                      </Alert>

                      <Box sx={{ 
                        background: 'rgba(0, 0, 0, 0.3)', 
                        p: 2, 
                        borderRadius: 2,
                        fontFamily: 'monospace',
                        fontSize: '0.9rem',
                        color: colors.white,
                      }}>
                        <Typography><strong>Beneficiary:</strong> {bankDetails.beneficiaryName}</Typography>
                        <Typography><strong>Bank:</strong> {bankDetails.bankName}</Typography>
                        <Typography><strong>Account Type:</strong> {bankDetails.accountType}</Typography>
                        <Typography><strong>Routing (ACH):</strong> {bankDetails.routingNumber}</Typography>
                        <Typography><strong>Routing (Wire):</strong> {bankDetails.wireRoutingNumber}</Typography>
                        <Typography><strong>Account #:</strong> {bankDetails.accountNumber}</Typography>
                        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.1)' }} />
                        <Typography sx={{ color: colors.accent }}>
                          <strong>Reference/Memo:</strong> {createdInvoice.invoiceId}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Step 3: Submit Proof */}
            <Paper
              sx={{
                p: 4,
                mt: 4,
                background: 'rgba(255, 255, 255, 0.03)',
                backdropFilter: 'blur(10px)',
                border: `1px solid rgba(255, 255, 255, 0.1)`,
                borderRadius: 3,
              }}
            >
              <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: colors.accent,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: colors.white,
                    fontWeight: 700,
                  }}
                >
                  2
                </Box>
                <Typography variant="h5" sx={{ color: colors.white, fontWeight: 600 }}>
                  Submit Proof of Payment
                </Typography>
              </Stack>

              <form onSubmit={handleProofSubmit}>
                <Grid container spacing={3}>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      required
                      label="Invoice ID"
                      value={proofForm.invoiceId}
                      onChange={(e) => setProofForm(prev => ({ ...prev, invoiceId: e.target.value }))}
                      sx={textFieldStyle}
                      disabled={!!createdInvoice}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Transaction Hash (for USDC)"
                      value={proofForm.txHash}
                      onChange={(e) => setProofForm(prev => ({ ...prev, txHash: e.target.value }))}
                      placeholder="0x..."
                      sx={textFieldStyle}
                      helperText="Ethereum transaction hash (0x + 64 characters)"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                      fullWidth
                      label="Reference Number (for Bank Transfer)"
                      value={proofForm.referenceNumber}
                      onChange={(e) => setProofForm(prev => ({ ...prev, referenceNumber: e.target.value }))}
                      sx={textFieldStyle}
                      helperText="ACH/Wire confirmation or reference number"
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 6 }}>
                    <Button
                      component="label"
                      fullWidth
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      sx={{
                        height: 56,
                        borderColor: colors.midGray,
                        color: proofFile ? colors.success : colors.white,
                        borderStyle: 'dashed',
                        '&:hover': {
                          borderColor: colors.teal,
                          background: 'rgba(0, 180, 216, 0.1)',
                        },
                      }}
                    >
                      {proofFile ? proofFile.name : 'Upload Screenshot/Receipt (Optional)'}
                      <input
                        type="file"
                        hidden
                        accept=".jpg,.jpeg,.png,.pdf,.gif,.webp"
                        onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      />
                    </Button>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Additional Notes (Optional)"
                      value={proofForm.notes}
                      onChange={(e) => setProofForm(prev => ({ ...prev, notes: e.target.value }))}
                      sx={textFieldStyle}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      size="large"
                      disabled={proofSubmitting}
                      startIcon={proofSubmitting ? <CircularProgress size={20} /> : <CheckIcon />}
                      sx={{
                        py: 2,
                        background: `linear-gradient(135deg, ${colors.success} 0%, #059669 100%)`,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                        '&:hover': {
                          background: `linear-gradient(135deg, #059669 0%, ${colors.success} 100%)`,
                        },
                      }}
                    >
                      {proofSubmitting ? 'Submitting...' : 'Submit Proof of Payment'}
                    </Button>
                  </Grid>
                </Grid>
              </form>

              {/* How to find TX hash */}
              <Accordion sx={{ 
                mt: 3, 
                background: 'rgba(0, 0, 0, 0.2)',
                border: 'none',
                '&:before': { display: 'none' },
              }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: colors.midGray }} />}>
                  <Typography sx={{ color: colors.midGray }}>
                    <InfoIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                    How to find your transaction hash
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography variant="body2" sx={{ color: colors.midGray }}>
                    1. After sending USDC, go to your wallet's transaction history.<br />
                    2. Find the transaction you just sent.<br />
                    3. Look for "Transaction Hash", "TX ID", or "TxHash".<br />
                    4. Copy the full hash (starts with 0x and is 66 characters long).<br />
                    5. You can also find it on{' '}
                    <MuiLink href="https://etherscan.io" target="_blank" sx={{ color: colors.teal }}>
                      Etherscan
                    </MuiLink>
                    {' '}by searching your wallet address.
                  </Typography>
                </AccordionDetails>
              </Accordion>
            </Paper>
          </>
        )}

        {/* Already have an invoice? */}
        {!createdInvoice && (
          <Paper
            sx={{
              p: 4,
              background: 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(10px)',
              border: `1px solid rgba(255, 255, 255, 0.1)`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h6" sx={{ color: colors.white, mb: 2 }}>
              Already have an invoice or made a payment?
            </Typography>
            <Typography variant="body2" sx={{ color: colors.midGray, mb: 3 }}>
              Enter your invoice ID to check status or submit proof of payment.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                label="Invoice ID"
                value={proofForm.invoiceId}
                onChange={(e) => setProofForm(prev => ({ ...prev, invoiceId: e.target.value }))}
                placeholder="REDEX-YYYYMMDD-XXXX"
                sx={textFieldStyle}
              />
              <Button
                variant="outlined"
                component={Link}
                to={`/payments/receipt/${proofForm.invoiceId}`}
                disabled={!proofForm.invoiceId}
                sx={{
                  minWidth: 150,
                  borderColor: colors.teal,
                  color: colors.teal,
                  '&:hover': {
                    borderColor: colors.tealLight,
                    background: 'rgba(0, 180, 216, 0.1)',
                  },
                }}
              >
                Check Status
              </Button>
            </Stack>
          </Paper>
        )}

        {/* Policies Section */}
        <Paper
          sx={{
            p: 4,
            mt: 4,
            background: 'rgba(255, 255, 255, 0.02)',
            border: `1px solid rgba(255, 255, 255, 0.05)`,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ color: colors.white, mb: 3 }}>
            Payment Policies
          </Typography>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: colors.teal, mb: 1 }}>
                Settlement Terms
              </Typography>
              <Typography variant="body2" sx={{ color: colors.midGray }}>
                {config?.policies.settlementTerms}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: colors.teal, mb: 1 }}>
                Refund Policy
              </Typography>
              <Typography variant="body2" sx={{ color: colors.midGray }}>
                {config?.policies.refundPolicy}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: colors.teal, mb: 1 }}>
                USDC Valuation
              </Typography>
              <Typography variant="body2" sx={{ color: colors.midGray }}>
                {config?.policies.usdcValuation}
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ color: colors.teal, mb: 1 }}>
                Disputes & Support
              </Typography>
              <Typography variant="body2" sx={{ color: colors.midGray }}>
                Contact us at{' '}
                <MuiLink href={`mailto:${config?.policies.disputeContact}`} sx={{ color: colors.accent }}>
                  {config?.policies.disputeContact}
                </MuiLink>
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      </Container>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Shared text field styling
const textFieldStyle = {
  '& .MuiOutlinedInput-root': {
    color: colors.white,
    '& fieldset': {
      borderColor: 'rgba(255, 255, 255, 0.2)',
    },
    '&:hover fieldset': {
      borderColor: colors.teal,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.teal,
    },
  },
  '& .MuiInputLabel-root': {
    color: colors.midGray,
    '&.Mui-focused': {
      color: colors.teal,
    },
  },
  '& .MuiFormHelperText-root': {
    color: colors.midGray,
  },
};

export default PaymentsPage;
